import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  GridLegacy as Grid,
  Paper,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close, CloudUpload, Delete, Image as ImageIcon, InsertDriveFile } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { api } from '../utils/api';
import type { Task } from '../types';

interface TaskReportDialogProps {
  open: boolean;
  onClose: () => void;
  task: Task | null;
  onReportSubmit: (taskId: number) => void;
}

export default function TaskReportDialog({ open, onClose, task, onReportSubmit }: TaskReportDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<{ file: File; preview: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setFiles([]);
      setFilePreviews([]);
      setDescription('');
      setIsDrawing(false);
    }
  }, [open]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    if (!task) return;

    // Filter files based on task type
    const taskType = task.taskType;
    const isFileType = taskType === 'Measure' || taskType === 'Shop Drawing';
    const isImageType = taskType === 'Fabrication' || taskType === 'Install';

    const validFiles = selectedFiles.filter(file => {
      if (isFileType) {
        // For Measure/Shop Drawing: Allow PDFs and images (will be converted to PDF if needed)
        return file.type === 'application/pdf' || file.type.startsWith('image/');
      } else if (isImageType) {
        // For Fabrication/Install: Only images
        return file.type.startsWith('image/');
      }
      return false;
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.warning('Some files were filtered out. Only allowed file types are accepted.');
    }

    if (validFiles.length === 0) return;

    // Add files and create previews for images
    const newFiles = [...files, ...validFiles];
    setFiles(newFiles);

    // Create previews for images
    const newPreviews = [...filePreviews];
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push({ file, preview: reader.result as string });
          setFilePreviews([...newPreviews]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    const newPreviews = [...filePreviews];
    const removedFile = newFiles[index];
    
    newFiles.splice(index, 1);
    
    // Remove corresponding preview
    const previewIndex = newPreviews.findIndex(p => p.file === removedFile);
    if (previewIndex !== -1) {
      newPreviews.splice(previewIndex, 1);
    }
    
    setFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  const handleSubmit = async () => {
    if (!task || !task.jobId) {
      toast.error('Task or job information is missing');
      return;
    }

    if (files.length === 0) {
      toast.error('Please upload at least one file or image');
      return;
    }

    setUploading(true);

    try {
      const taskType = task.taskType;
      const isFileType = taskType === 'Measure' || taskType === 'Shop Drawing';
      const isImageType = taskType === 'Fabrication' || taskType === 'Install';

      if (isFileType) {
        // For Measure/Shop Drawing: Upload files to job files first, then create report
        const uploadedFileIds = [];
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('isDrawing', (isDrawing || taskType === 'Shop Drawing') ? 'true' : 'false');
          if (description.trim()) {
            formData.append('customName', description.trim());
          }

          const fileResponse = await api.post(`/jobs/${task.jobId}/files`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          uploadedFileIds.push(fileResponse.data.file.id);
        }

        // Save worker report record and link the uploaded file IDs
        await api.post(`/jobs/${task.jobId}/worker-reports`, {
          task_id: task.id,
          report_type: 'file',
          description: description || null,
          file_ids: uploadedFileIds,
        });
      } else if (isImageType) {
        // For Fabrication/Install: Upload images to worker history
        const formData = new FormData();
        files.forEach((file) => {
          formData.append(`images`, file);
        });
        if (description.trim()) {
          formData.append('description', description.trim());
        }
        formData.append('task_id', task.id.toString());

        await api.post(`/jobs/${task.jobId}/worker-reports`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      onReportSubmit(task.id);
      onClose();
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast.error(error.response?.data?.message || 'Failed to submit report');
    } finally {
      setUploading(false);
    }
  };

  if (!task) return null;

  const taskType = task.taskType;
  const isFileType = taskType === 'Measure' || taskType === 'Shop Drawing';
  const isImageType = taskType === 'Fabrication' || taskType === 'Install';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Submit Work Report</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Task: {task.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Type: {taskType}
          </Typography>

          {isFileType && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload files (PDF or images) that will be saved to the job's file list.
            </Typography>
          )}

          {isImageType && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload images that will be saved to the job's worker history.
            </Typography>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={isImageType ? 'image/*' : 'image/*,application/pdf'}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ mb: 2 }}
          >
            {isImageType ? 'Upload Images' : 'Upload Files'}
          </Button>

          {isFileType && files.some(f => f.type.startsWith('image/')) && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDrawing}
                    onChange={(e) => setIsDrawing(e.target.checked)}
                  />
                }
                label="This is a drawing (will be converted to PDF)"
              />
            </Box>
          )}

          {files.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Files ({files.length}):
              </Typography>
              <Grid container spacing={2}>
                {files.map((file, index) => {
                  const preview = filePreviews.find(p => p.file === file);
                  const isImage = file.type.startsWith('image/');
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper
                        sx={{
                          p: 1,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        {preview && isImage ? (
                          <Box
                            component="img"
                            src={preview.preview}
                            alt={file.name}
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 1,
                              mb: 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 150,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            {isImage ? (
                              <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                            ) : (
                              <InsertDriveFile sx={{ fontSize: 48, color: 'grey.400' }} />
                            )}
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            mb: 1,
                          }}
                        >
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFile(index)}
                          sx={{ position: 'absolute', top: 4, right: 4 }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={uploading || files.length === 0}
          startIcon={uploading ? <CircularProgress size={20} /> : null}
        >
          {uploading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
